import { tr } from '../services/i18n.js';

export const SOCIAL_FORMATS = {
  square: { id: 'square', label: 'Carré', width: 1080, height: 1080, ratio: '1:1' },
  story: { id: 'story', label: 'Story', width: 1080, height: 1920, ratio: '9:16' },
  og: { id: 'og', label: 'Aperçu lien', width: 1200, height: 630, ratio: '1.91:1' }
};

export const SOCIAL_PLATFORMS = {
  x: {
    id: 'x',
    label: 'X / Twitter',
    format: 'square',
    maxText: 240,
    hashtags: ['fooview', 'Football', 'Tactique']
  },
  instagram: {
    id: 'instagram',
    label: 'Instagram',
    format: 'story',
    maxText: 180,
    hashtags: ['fooview', 'AnalyseTactique', 'Football']
  },
  tiktok: {
    id: 'tiktok',
    label: 'TikTok',
    format: 'story',
    maxText: 150,
    hashtags: ['fooview', 'FootTok', 'Tactique']
  },
  discord: {
    id: 'discord',
    label: 'Discord',
    format: 'square',
    maxText: 420,
    hashtags: []
  },
  linkedin: {
    id: 'linkedin',
    label: 'LinkedIn',
    format: 'og',
    maxText: 500,
    hashtags: ['SportTech', 'Football', 'IA']
  },
  facebook: {
    id: 'facebook',
    label: 'Facebook',
    format: 'og',
    maxText: 350,
    hashtags: ['fooview', 'Football']
  }
};

export function listSocialPlatforms() {
  return Object.values(SOCIAL_PLATFORMS);
}

export function getSocialPlatform(id) {
  return SOCIAL_PLATFORMS[id] || SOCIAL_PLATFORMS.x;
}

export function getSocialFormat(id) {
  return SOCIAL_FORMATS[id] || SOCIAL_FORMATS.square;
}

export function buildSocialSharePayload(debrief, { platform = 'x', url = '' } = {}) {
  const p = getSocialPlatform(platform);
  const format = localizedFormat(getSocialFormat(p.format));
  const title = cleanLine(debrief?.title || tr('Analyse fooview', 'fooview analysis'));
  const summary = cleanLine(debrief?.summary || debrief?.key_moment || tr('Débrief tactique généré avec fooview.', 'Tactical debrief generated with fooview.'));
  const match = debrief?.match ? `${debrief.match.home_team || ''} - ${debrief.match.away_team || ''}`.trim() : '';
  const competition = debrief?.competition?.name || '';
  const hashtags = p.hashtags.map((tag) => `#${tag}`).join(' ');
  const text = buildPlatformText({ platform: p.id, title, summary, match, competition, hashtags, url, maxText: p.maxText });

  return {
    platform: p,
    format,
    title,
    summary,
    match,
    competition,
    url,
    text,
    imageTitle: truncateText(title, 80),
    imageSubtitle: truncateText(match || competition || summary, 120)
  };
}

export function buildScoreSharePayload({ rows = [], scopeLabel = 'Global', competitionName = '' } = {}, { platform = 'x', url = '' } = {}) {
  const p = getSocialPlatform(platform);
  const format = localizedFormat(getSocialFormat(p.format));
  const topRows = rows.slice(0, 3);
  const title = cleanLine(`${tr('Scores fooview', 'fooview scores')} - ${scopeLabel}`);
  const summary = cleanLine(buildScoreSummary(topRows, competitionName));
  const hashtags = p.hashtags.map((tag) => `#${tag}`).join(' ');
  const text = buildPlatformText({ platform: p.id, title, summary, competition: competitionName, hashtags, url, maxText: p.maxText });

  return {
    platform: p,
    format,
    title,
    summary,
    match: '',
    competition: competitionName || scopeLabel,
    url,
    text,
    rows: topRows,
    imageTitle: truncateText(title, 80),
    imageSubtitle: truncateText(summary, 120)
  };
}

export function buildPlatformText({ platform = 'x', title = '', summary = '', match = '', competition = '', hashtags = '', url = '', maxText = 280 } = {}) {
  const context = match && competition ? `${match} · ${competition}` : match || competition;
  const templates = {
    x: [title, context, summary, hashtags, url],
    instagram: [title, context, summary, hashtags, url ? tr('Lien dans fooview', 'Link in fooview') : ''],
    tiktok: [title, context, tr('Analyse ton œil tactique avant la réponse IA.', 'Test your tactical eye before the AI answer.'), hashtags],
    discord: [`**${title}**`, context, summary, url],
    linkedin: [title, context, tr(`Analyse tactique générée avec fooview : ${summary}`, `Tactical analysis generated with fooview: ${summary}`), hashtags, url],
    facebook: [title, context, summary, hashtags, url]
  };
  return truncateText((templates[platform] || templates.x).filter(Boolean).join('\n'), maxText);
}

function buildScoreSummary(rows, competitionName) {
  if (!rows.length) return competitionName ? tr(`Classement ${competitionName} à suivre.`, `${competitionName} ranking to follow.`) : tr('Classement des joueurs à suivre.', 'Player ranking to follow.');
  const podium = rows.map((row, index) => `#${index + 1} ${row.pseudo} (${row.points} pts)`).join(' · ');
  return competitionName ? `${competitionName} : ${podium}` : podium;
}

function localizedFormat(format) {
  if (format.id === 'square') return { ...format, label: tr('Carré', 'Square') };
  if (format.id === 'og') return { ...format, label: tr('Aperçu lien', 'Link preview') };
  return format;
}

export function buildPlatformShareUrl(platform, { text = '', url = '' } = {}) {
  const encodedText = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(url);
  if (platform === 'x') return `https://twitter.com/intent/tweet?text=${encodedText}`;
  if (platform === 'linkedin') return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
  if (platform === 'facebook') return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
  if (platform === 'discord') return '';
  if (platform === 'instagram' || platform === 'tiktok') return '';
  return '';
}

export function platformShareMode(platform) {
  if (['x', 'linkedin', 'facebook'].includes(platform)) return 'intent';
  if (platform === 'discord') return 'copy';
  return 'download';
}

export function truncateText(value, max = 280) {
  const text = cleanLine(value);
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}

function cleanLine(value) {
  return String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim();
}
